import {
    ArrowLeftOutlined,
    CheckCircleFilled,
    CrownOutlined,
    InfoCircleOutlined,
    LockOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { Button, Drawer, message } from 'antd';
import cx from 'classnames';
import { ListTree } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    QUERY_TYPE_LABEL,
    createOrder,
    getChapterPrices,
    getOrderInfo,
    previewNext,
    rememberOrder,
} from '../../api';
import PayDialog from './PayDialog';
import PrepStatus from './PrepStatus';
import PreviewMarkdown from './PreviewMarkdown';
import {
    addonKey,
    addonsFromQuote,
    buildChapters,
    buildPreviewUnits,
    buildRecreatePayload,
    normTitle,
    portalContainer,
    predictNextUnit,
    sameUnit,
    toAddonsPayload,
    toUnit,
    unitKind,
} from './utils';
import styles from './index.module.scss';

export { default as WritingPayModal } from './PayDialog';

/**
 * 新订单进入预览后自动预览的节数：有摘要时两节（摘要 → Abstract，再接首节正文），
 * 没有摘要的类型（开题报告 / 文献综述）只预览一节，免得一进来就铺两大段；之后由用户点「继续预览」
 */
const AUTO_PREVIEWS_WITH_ABSTRACT = 2;
const autoPreviewCount = (units) => (unitKind(units[0]) === 'abstract-zh' ? AUTO_PREVIEWS_WITH_ABSTRACT : 1);
const DEFAULT_MAX_PREVIEW = 3;
/** 打字跟随：距底部不超过这个距离时视为正在跟读 */
const FOLLOW_THRESHOLD = 160;
const PAID_MODES = ['FULL', 'CHAPTER_TO_FULL'];
/** 后端答「预览正在生成」（旧服务 / 生成在别的实例上）时，隔几秒重新连接，最多等约 5 分钟 */
const BUSY_RETRY_MS = 5000;
const MAX_BUSY_RETRIES = 60;
/** 流中途断开（网络抖动）时，后台生成不受影响，短暂等待后接回去 */
const DROP_RETRY_MS = 1500;
const MAX_DROP_RETRIES = 3;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isBusyError = (error) => /预览正在生成/.test(error?.message || '');
const isDropError = (error) => error?.name === 'TypeError' || /连接中断/.test(error?.message || '');

const previewedList = (data) =>
    (Array.isArray(data?.previewed) ? data.previewed : []).filter(
        (item) => item && item.chapterTitle,
    );

/**
 * 后台是否还有一节预览在生成。previewCount 从 1 起、开始生成时 +1、失败回退，
 * 所以「previewCount - 1（已开始）> 已完成节数」就说明有一节还没写完；后端给了显式字段时以它为准。
 */
const isPreviewRunning = (data, finished) => {
    if (typeof data?.previewRunning === 'boolean') return data.previewRunning;
    const count = Number(data?.previewCount);
    return Number.isFinite(count) && count - 1 > finished;
};

const matchesChapter = (unit, chapter) => {
    if (!unit) return false;
    if (chapter.kind === 'abstract-zh' || chapter.kind === 'abstract-en')
        return unitKind(unit) === chapter.kind;
    return normTitle(unit.chapterTitle) === normTitle(chapter.title);
};

const matchesSection = (unit, chapter, section) =>
    matchesChapter(unit, chapter) && normTitle(unit.sectionTitle) === normTitle(section.title);

/** 正文开头重复了本节标题（如 `### 1.1 研究背景`）时去掉，标题由稿纸统一排版 */
const stripLeadingTitle = (content, unit) => {
    if (!content || !unit) return content || '';
    const match = content.match(/^\s*#{1,6}\s*([^\n]+)\n?/);
    if (!match) return content;
    const heading = normTitle(match[1].replace(/[*_]/g, ''));
    const titles = [unit.chapterTitle, unit.sectionTitle, unit.subsectionTitle]
        .filter(Boolean)
        .map(normTitle);
    return titles.includes(heading) ? content.slice(match[0].length) : content;
};

function PreviewSession({
    quote: quoteProp,
    title,
    queryType,
    model,
    outlines: outlinesProp,
    onBack,
    onQuoteChange,
    createPayload,
    ownUids,
}) {
    const navigate = useNavigate();
    const [activeQuote, setActiveQuote] = useState(quoteProp);
    const activeQuoteRef = useRef(quoteProp);
    useEffect(() => {
        if (quoteProp && quoteProp !== activeQuoteRef.current) {
            activeQuoteRef.current = quoteProp;
            setActiveQuote(quoteProp);
        }
    }, [quoteProp]);

    const uid = activeQuote?.uid;
    const uidRef = useRef(uid);
    uidRef.current = uid;

    const [info, setInfo] = useState(null);
    const infoRef = useRef(null);
    const outlines = useMemo(
        () =>
            Array.isArray(outlinesProp) && outlinesProp.length
                ? outlinesProp
                : Array.isArray(info?.outlines)
                  ? info.outlines
                  : [],
        [outlinesProp, info],
    );
    const chapters = useMemo(() => buildChapters(outlines), [outlines]);
    const bodyChapters = useMemo(() => chapters.filter((c) => c.kind === 'body'), [chapters]);
    const units = useMemo(() => buildPreviewUnits(chapters), [chapters]);
    const unitsRef = useRef(units);
    unitsRef.current = units;

    const docTitle = title || activeQuote?.paperTitle || info?.paperTitle || '';
    const effectiveType = queryType || activeQuote?.queryType || info?.queryType || 'PAPER';
    const typeLabel = QUERY_TYPE_LABEL[effectiveType] || '毕业长文';
    const purchaseMode = info?.purchaseMode || 'UNPAID';
    const payMode = purchaseMode === 'CHAPTER' ? 'upgrade' : 'unlock';

    const [blocks, setBlocks] = useState([]);
    const [typingId, setTypingId] = useState(null);
    const [previewError, setPreviewError] = useState('');
    /** 等待重新连接时的友好提示（不是错误） */
    const [notice, setNotice] = useState('');
    const [doneCount, setDoneCount] = useState(0);
    const [maxPreview, setMaxPreview] = useState(DEFAULT_MAX_PREVIEW);
    const [chapterPrices, setChapterPrices] = useState([]);
    const [pay, setPay] = useState({ open: false, plan: 'full', chapter: null });
    const [tocOpen, setTocOpen] = useState(false);

    const mountedRef = useRef(true);
    const typingRef = useRef(null);
    const abortRef = useRef(null);
    const seqRef = useRef(0);
    const doneUnitsRef = useRef([]);
    const maxPreviewRef = useRef(DEFAULT_MAX_PREVIEW);
    /** 调度循环是否在跑：同一时间只允许一个，避免重复开预览 */
    const drivingRef = useRef(false);
    /** 本次进入时订单还没有任何已完成的预览：只有这种情况才自动连续预览 */
    const autoChainRef = useRef(null);
    /** 最近一次错误来自读订单进度（重试时只重读）还是预览本身（重试时再预览一节） */
    const errorKindRef = useRef('preview');
    const contentAreaRef = useRef(null);
    const quoteCacheRef = useRef(null);
    if (!quoteCacheRef.current) {
        quoteCacheRef.current = new Map();
        if (quoteProp?.uid)
            quoteCacheRef.current.set(addonKey(addonsFromQuote(quoteProp)), quoteProp);
    }

    /** 用订单详情同步预览次数与已完成的小节（后端内容已排好版）；正在输出时不动稿纸 */
    const applyInfo = useCallback((data) => {
        infoRef.current = data;
        setInfo(data);
        const max = Number(data.maxPreview) > 0 ? Number(data.maxPreview) : DEFAULT_MAX_PREVIEW;
        maxPreviewRef.current = max;
        setMaxPreview(max);
        const previewed = previewedList(data);
        if (typingRef.current || previewed.length < doneUnitsRef.current.length) return;
        const units = previewed.map(toUnit);
        setBlocks((prev) =>
            previewed.map((item, index) => {
                const same = prev.find((b) =>
                    item.detailId != null && b.detailId != null
                        ? b.detailId === item.detailId
                        : sameUnit(b.unit, units[index]),
                );
                return {
                    id: same?.id || `pv-r${item.detailId ?? ''}-${++seqRef.current}`,
                    unit: units[index],
                    content: item.content || same?.content || '',
                    detailId: item.detailId,
                };
            }),
        );
        doneUnitsRef.current = units;
        setDoneCount(units.length);
    }, []);

    const loadInfo = useCallback(async () => {
        const currentUid = uidRef.current;
        if (!currentUid) return null;
        try {
            const data = await getOrderInfo(currentUid);
            if (!mountedRef.current || !data) return null;
            applyInfo(data);
            return data;
        } catch {
            return null;
        }
    }, [applyInfo]);

    /**
     * 流式读取一节预览。resume 表示接上后台正在生成的那一节：后端先补发已生成的全部内容再继续推送，
     * 所以每次连接都从空内容开始拼接。
     * 返回 'ok' | 'busy'（预览正在生成，稍后重连）| 'dropped'（连接中断）| 'fail' | 'aborted' | 'skip'。
     */
    const streamPreview = useCallback(async (resume = false) => {
        if (typingRef.current) return 'skip';
        const currentUid = uidRef.current;
        if (!currentUid) {
            errorKindRef.current = 'info';
            setPreviewError('订单信息缺失，无法预览');
            return 'fail';
        }
        const id = `pv-${++seqRef.current}`;
        const predicted = predictNextUnit(unitsRef.current, doneUnitsRef.current);
        typingRef.current = id;
        setPreviewError('');
        setTypingId(id);
        setBlocks((prev) => [...prev, { id, unit: predicted, content: '', resume }]);

        const controller = new AbortController();
        abortRef.current = controller;
        let buffer = '';
        let frame = 0;
        const flush = () => {
            frame = 0;
            if (!buffer) return;
            const chunk = buffer;
            buffer = '';
            setBlocks((prev) =>
                prev.map((b) => (b.id === id ? { ...b, content: b.content + chunk } : b)),
            );
        };
        try {
            const done = await previewNext(currentUid, {
                signal: controller.signal,
                onToken: (token) => {
                    buffer += token;
                    if (!frame) frame = window.requestAnimationFrame(flush);
                },
            });
            if (frame) window.cancelAnimationFrame(frame);
            if (controller.signal.aborted || !mountedRef.current) return 'aborted';
            flush();
            const unit = toUnit(done) || predicted;
            // 完成事件带回排好版（引用已重新编号）的正文时，以它替换流式拼出的原文
            const finalContent =
                typeof done?.content === 'string' && done.content.trim() ? done.content : null;
            setBlocks((prev) =>
                prev.map((b) =>
                    b.id === id
                        ? {
                              ...b,
                              unit,
                              detailId: done?.detailId,
                              resume: false,
                              ...(finalContent != null && { content: finalContent }),
                          }
                        : b,
                ),
            );
            doneUnitsRef.current = [...doneUnitsRef.current, unit];
            setDoneCount(doneUnitsRef.current.length);
            return 'ok';
        } catch (error) {
            if (frame) window.cancelAnimationFrame(frame);
            if (controller.signal.aborted || !mountedRef.current) return 'aborted';
            setBlocks((prev) => prev.filter((b) => b.id !== id));
            if (isBusyError(error)) return 'busy';
            if (isDropError(error)) return 'dropped';
            errorKindRef.current = 'preview';
            setPreviewError(error?.message || '本节预览生成未完成，请重试');
            return 'fail';
        } finally {
            if (typingRef.current === id) {
                typingRef.current = null;
                if (mountedRef.current) setTypingId(null);
            }
            if (abortRef.current === controller) abortRef.current = null;
        }
    }, []);

    /**
     * 预览调度（同一时间只跑一个）。每一轮先读订单进度：
     * - 后台还有一节在生成（例如返回修改大纲后又回来）：接上它，不新开、不多扣免费次数；
     * - 否则只在「新订单自动预览前 autoPreviewCount 节」或用户点了「继续预览 / 重试」时才新开一节；
     * - 后端答「预览正在生成」：提示后隔几秒重连，而不是报错；流中途断开也自动接回。
     */
    const drive = useCallback(
        async (wantNew = false) => {
            if (drivingRef.current || typingRef.current) return;
            drivingRef.current = true;
            let pendingNew = wantNew;
            let busyRetries = 0;
            let dropRetries = 0;
            // 断线前已完成的节数：重连时发现那节既没写完也不在生成，说明生成失败了
            let droppedAt = null;
            try {
                while (mountedRef.current) {
                    const data = await loadInfo();
                    if (!mountedRef.current) return;
                    const current = data || infoRef.current;
                    if (!current) {
                        errorKindRef.current = 'info';
                        setPreviewError('读取预览进度失败，请检查网络后重试');
                        return;
                    }
                    if (current.purchaseMode && current.purchaseMode !== 'UNPAID') return;
                    const finished = doneUnitsRef.current.length;
                    if (autoChainRef.current == null) autoChainRef.current = finished === 0;

                    let resume = false;
                    if (data && isPreviewRunning(data, finished)) {
                        resume = true;
                        // 正在生成的这一节就是用户要的「下一节」
                        pendingNew = false;
                    } else {
                        const hasNext =
                            !unitsRef.current.length ||
                            !!predictNextUnit(unitsRef.current, doneUnitsRef.current);
                        const wanted =
                            pendingNew ||
                            (autoChainRef.current && finished < autoPreviewCount(unitsRef.current));
                        if (!wanted || !hasNext || finished >= maxPreviewRef.current) {
                            if (droppedAt != null && finished <= droppedAt) {
                                errorKindRef.current = 'preview';
                                setPreviewError(
                                    data
                                        ? '本节预览生成未完成，请重试'
                                        : '预览连接中断，请检查网络后重试',
                                );
                            }
                            return;
                        }
                    }

                    setNotice('');
                    const result = await streamPreview(resume);
                    if (result === 'ok') {
                        pendingNew = false;
                        busyRetries = 0;
                        dropRetries = 0;
                        droppedAt = null;
                        continue;
                    }
                    if (result === 'busy') {
                        busyRetries += 1;
                        if (busyRetries > MAX_BUSY_RETRIES) {
                            errorKindRef.current = 'preview';
                            setPreviewError('上一节预览仍在生成，请稍后点击重试');
                            return;
                        }
                        setNotice('上一节预览还在生成，几秒后自动重新连接…');
                        await wait(BUSY_RETRY_MS);
                    } else if (result === 'dropped') {
                        dropRetries += 1;
                        if (dropRetries > MAX_DROP_RETRIES) {
                            errorKindRef.current = 'preview';
                            setPreviewError('预览连接中断，请检查网络后重试');
                            return;
                        }
                        if (droppedAt == null) droppedAt = finished;
                        setNotice('预览连接中断，正在重新连接…');
                        await wait(DROP_RETRY_MS);
                    } else {
                        return;
                    }
                }
            } finally {
                drivingRef.current = false;
                if (mountedRef.current) setNotice('');
            }
        },
        [loadInfo, streamPreview],
    );

    // 进入预览：读订单进度，恢复已预览的小节，接上正在生成的一节，新订单直接开始首节预览
    useEffect(() => {
        mountedRef.current = true;
        drive(false);

        const { body } = document;
        const prevOverflow = body.style.overflow;
        body.style.overflow = 'hidden';
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
            body.style.overflow = prevOverflow;
        };
    }, []);

    // 单章解锁状态 / 价格（付款弹窗用）
    useEffect(() => {
        if (!uid) return undefined;
        let alive = true;
        getChapterPrices(uid)
            .then((list) => alive && setChapterPrices(Array.isArray(list) ? list : []))
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [uid]);

    // 打字时跟随最新内容：只在用户本来就贴近底部时才滚
    const activeContent = useMemo(
        () => blocks.find((b) => b.id === typingId)?.content ?? '',
        [blocks, typingId],
    );
    useEffect(() => {
        if (!typingId) return;
        const el = contentAreaRef.current;
        if (!el) return;
        if (el.scrollHeight - el.clientHeight - el.scrollTop <= FOLLOW_THRESHOLD) {
            el.scrollTop = el.scrollHeight;
        }
    }, [typingId, activeContent]);

    const doneBlocks = useMemo(() => blocks.filter((b) => b.id !== typingId), [blocks, typingId]);
    const typingBlock = typingId ? blocks.find((b) => b.id === typingId) : null;
    const hasNext =
        units.length === 0 ||
        !!predictNextUnit(
            units,
            doneBlocks.map((b) => b.unit),
        );
    const freeLeft = Math.max(0, maxPreview - doneCount);
    const canContinue = freeLeft > 0 && hasNext && purchaseMode === 'UNPAID' && !notice;

    const handleContinue = useCallback(() => {
        if (typingRef.current) return;
        if (doneUnitsRef.current.length >= maxPreviewRef.current) {
            message.info('免费预览次数已用完，请解锁查看完整内容');
            return;
        }
        drive(true);
    }, [drive]);

    const handleRetry = useCallback(() => {
        setPreviewError('');
        // 读进度失败只重读；预览失败则按用户意愿再预览一节（后台仍在生成时会接上它）
        drive(errorKindRef.current !== 'info');
    }, [drive]);

    const handleBack = () => {
        abortRef.current?.abort();
        onBack?.();
    };

    const openPay = useCallback((plan = 'full', chapter = null) => {
        setTocOpen(false);
        setPay({ open: true, plan, chapter });
    }, []);

    // 付款弹窗改附加服务：按新选项重新建单（带 fromUid），同一组合只建一次
    const recreate = useCallback(
        async (selected) => {
            const key = addonKey(selected);
            const cache = quoteCacheRef.current;
            let next = cache.get(key);
            if (!next) {
                const current = activeQuoteRef.current;
                next = await createOrder(
                    buildRecreatePayload({
                        createPayload,
                        title: docTitle,
                        queryType: effectiveType,
                        model,
                        outlines,
                        quote: current,
                        addons: toAddonsPayload(selected),
                        fromUid: current?.uid,
                    }),
                );
                if (!next?.uid) throw new Error('更新订单失败，请重试');
                cache.set(key, next);
            }
            if (!mountedRef.current) return next;
            ownUids.current.add(next.uid);
            const previous = activeQuoteRef.current;
            activeQuoteRef.current = next;
            setActiveQuote(next);
            onQuoteChange?.(next);
            // 改附加服务后的新订单替换首页记录里的旧订单（同一篇只留一条待支付）
            rememberOrder({
                no: next.orderNo || next.uid,
                uid: next.uid,
                title: docTitle,
                kind: 'WRITING',
                queryType: effectiveType,
                status: 'UNPAID',
                supersedes: previous?.uid !== next.uid ? previous?.uid : undefined,
            });
            return next;
        },
        [createPayload, docTitle, effectiveType, model, outlines, onQuoteChange, ownUids],
    );

    const scrollToBlock = (blockId) => {
        const el = document.getElementById(`wp-${blockId}`);
        const box = contentAreaRef.current;
        if (!el || !box) return;
        const top =
            box.scrollTop + el.getBoundingClientRect().top - box.getBoundingClientRect().top - 20;
        box.scrollTo({ top, behavior: 'smooth' });
    };

    // ==================== 目录 ====================

    const priceMap = useMemo(() => {
        const map = new Map();
        chapterPrices.forEach((item) => {
            if (item?.chapterTitle) map.set(normTitle(item.chapterTitle), item);
        });
        return map;
    }, [chapterPrices]);

    const chapterState = (chapter) => {
        if (priceMap.get(normTitle(chapter.title))?.unlocked) return 'unlocked';
        if (typingBlock && matchesChapter(typingBlock.unit, chapter)) return 'typing';
        if (doneBlocks.some((b) => matchesChapter(b.unit, chapter))) return 'previewed';
        return 'locked';
    };

    const sectionState = (chapter, section, chState) => {
        if (typingBlock && matchesSection(typingBlock.unit, chapter, section)) return 'typing';
        if (doneBlocks.some((b) => matchesSection(b.unit, chapter, section))) return 'previewed';
        return chState === 'unlocked' ? 'unlocked' : 'locked';
    };

    const handleTocClick = (chapter, section, state) => {
        if (state === 'typing' || state === 'previewed') {
            const target =
                state === 'typing'
                    ? typingBlock
                    : doneBlocks.find((b) =>
                          section
                              ? matchesSection(b.unit, chapter, section)
                              : matchesChapter(b.unit, chapter),
                      );
            if (target) {
                setTocOpen(false);
                scrollToBlock(target.id);
            }
            return;
        }
        if (state === 'unlocked') {
            message.info('该章已解锁，可在订单页查看完整内容');
            return;
        }
        const priceInfo = priceMap.get(normTitle(chapter.title));
        if (
            chapter.kind === 'body' &&
            priceInfo &&
            priceInfo.unlockable !== false &&
            payMode === 'unlock'
        )
            openPay('single', priceInfo.chapterTitle);
        else openPay('full');
    };

    const renderTocItem = ({ key, label, level, state, meta, tip, onClick }) => (
        <button
            key={key}
            type="button"
            className={styles.tocItem}
            data-state={state}
            data-level={level}
            title={tip || label}
            onClick={onClick}
        >
            <span className={styles.tocRail}>
                <span className={styles.tocMark}>
                    {state === 'locked' && level === 'chapter' ? <LockOutlined /> : null}
                </span>
            </span>
            <span className={styles.tocLabel}>{label}</span>
            {meta ? <span className={styles.tocMeta}>{meta}</span> : null}
        </button>
    );

    // 目录只标状态（生成中 / 已解锁），不显示单章价格；价格在付款弹窗里看
    const tocItems = [];
    chapters.forEach((chapter) => {
        const state = chapterState(chapter);
        let meta = null;
        if (state === 'typing') meta = '生成中';
        else if (state === 'unlocked') meta = '已解锁';
        let label = chapter.title;
        if (chapter.kind === 'abstract-zh') label = '摘要';
        if (chapter.kind === 'abstract-en') label = 'Abstract';
        tocItems.push(
            renderTocItem({
                key: chapter.key,
                label,
                level: 'chapter',
                state,
                meta,
                onClick: () => handleTocClick(chapter, null, state),
            }),
        );
        chapter.sections.forEach((section) => {
            const secState = sectionState(chapter, section, state);
            tocItems.push(
                renderTocItem({
                    key: section.key,
                    label: section.title,
                    level: 'section',
                    state: secState,
                    meta: secState === 'typing' ? '生成中' : null,
                    onClick: () => handleTocClick(chapter, section, secState),
                }),
            );
        });
    });

    const unlockedCount = bodyChapters.filter(
        (c) => priceMap.get(normTitle(c.title))?.unlocked,
    ).length;
    const lockedCount = Math.max(0, bodyChapters.length - unlockedCount);
    const words = Number(activeQuote?.totalWords);

    const renderToc = () => (
        <aside className={styles.toc}>
            <div className={styles.tocHead}>
                <span className={styles.tocHeadTitle}>目录</span>
                <span className={styles.tocCount}>已预览 {doneCount} 节</span>
            </div>
            <div className={styles.tocList}>{tocItems}</div>
            <div className={styles.tocFoot}>
                <div className={styles.tocStat}>
                    <span className={styles.tocStatLabel}>章节数</span>
                    <span className={styles.tocStatValue}>{bodyChapters.length} 章</span>
                </div>
                <div className={styles.tocStat}>
                    <span className={styles.tocStatLabel}>预计字数</span>
                    <span className={styles.tocStatValue}>
                        {Number.isFinite(words) && words > 0
                            ? `约 ${words.toLocaleString('zh-CN')} 字`
                            : '--'}
                    </span>
                </div>
                <div className={styles.tocStat}>
                    <span className={styles.tocStatLabel}>
                        {unlockedCount > 0 ? '已解锁章节' : '已预览段落'}
                    </span>
                    <span className={styles.tocStatValue} data-tone="green">
                        {unlockedCount > 0
                            ? `${unlockedCount} / ${bodyChapters.length}`
                            : `${doneCount} 段`}
                    </span>
                </div>
                <div className={styles.tocStat}>
                    <span className={styles.tocStatLabel}>免费预览次数</span>
                    <span className={styles.tocStatValue} data-tone="brand">
                        {doneCount} / {maxPreview}
                    </span>
                </div>
                <div className={styles.tocFootTip}>
                    <InfoCircleOutlined />
                    内容由 AI 生成，仅供参考
                </div>
            </div>
        </aside>
    );

    // ==================== 稿纸 ====================

    const renderWaiting = (block, index) => {
        if (block.resume) return <PrepStatus resume />;
        // 首节出字前展示准备步骤，之后的节只要一个轻量提示
        if (index === 0) return <PrepStatus />;
        return <div className={styles.waiting}>正在生成本节预览…</div>;
    };

    const renderBlocks = () => {
        let prevChapter = null;
        let prevSection = null;
        return blocks.map((block, index) => {
            const { unit } = block;
            const kind = unitKind(unit);
            const isTyping = block.id === typingId;
            const headings = [];
            if (kind === 'abstract-zh') {
                headings.push(
                    <h2 key="h" className={styles.h2Abstract}>
                        摘　要
                    </h2>,
                );
                prevChapter = kind;
                prevSection = null;
            } else if (kind === 'abstract-en') {
                headings.push(
                    <h2 key="h" className={cx(styles.h2Abstract, styles.h2AbstractEn)}>
                        Abstract
                    </h2>,
                );
                prevChapter = kind;
                prevSection = null;
            } else if (unit) {
                const chapterKey = normTitle(unit.chapterTitle);
                if (chapterKey !== prevChapter) {
                    headings.push(
                        <h2 key="c" className={styles.h2Center}>
                            {unit.chapterTitle}
                        </h2>,
                    );
                    prevSection = null;
                }
                const sectionKey = unit.sectionTitle ? normTitle(unit.sectionTitle) : null;
                if (sectionKey && sectionKey !== prevSection) {
                    headings.push(
                        <h3 key="s" className={styles.h3}>
                            {unit.sectionTitle}
                        </h3>,
                    );
                }
                if (unit.subsectionTitle) {
                    headings.push(
                        <h4 key="t" className={styles.h4}>
                            {unit.subsectionTitle}
                        </h4>,
                    );
                }
                prevChapter = chapterKey;
                prevSection = sectionKey;
            }
            const content = stripLeadingTitle(block.content, unit);
            return (
                <section key={block.id} id={`wp-${block.id}`} className={styles.block}>
                    {headings}
                    {content ? (
                        <PreviewMarkdown
                            content={content}
                            typing={isTyping}
                            lang={kind === 'abstract-en' ? 'en' : 'zh'}
                        />
                    ) : isTyping ? (
                        renderWaiting(block, index)
                    ) : null}
                </section>
            );
        });
    };

    // 折页上的说明：价格在解锁弹窗里看，这里只提示免登录与查单方式
    const renderPaywallPrice = () => {
        if (payMode === 'upgrade') {
            return <span>补差价解锁全文，已解锁的章节不会重复收费</span>;
        }
        return <span className={styles.paywallPriceNote}>免登录，凭订单号查看进度与下载</span>;
    };

    // 默认赠送的图 / 表（开题报告的技术路线图、研究计划表）：折页上提一句，价格仍在解锁弹窗里看
    const giftFigures = Number(activeQuote?.freeFigureCount) || 0;
    const giftTables = Number(activeQuote?.freeTableCount) || 0;
    let giftText = '';
    if (payMode !== 'upgrade' && giftFigures + giftTables > 0) {
        giftText =
            activeQuote?.queryType === 'OPENING_REPORT'
                ? `已赠送 ${giftFigures} 张技术路线图、${giftTables} 张研究计划表，限时特惠中`
                : `已赠送 ${giftFigures} 图 ${giftTables} 表，限时特惠中`;
    }

    const renderPaywall = () => (
        <div className={styles.paywall}>
            <div className={styles.paywallFade} />
            <div className={styles.paywallCard}>
                <span className={styles.paywallIcon}>
                    <LockOutlined />
                </span>
                <div className={styles.paywallText}>
                    <h3 className={styles.paywallTitle}>
                        {lockedCount > 0
                            ? `后面还有 ${lockedCount} 章待解锁`
                            : '解锁后查看完整全文'}
                    </h3>
                    <p className={styles.paywallDesc}>
                        {freeLeft > 0 && hasNext
                            ? `还可免费预览 ${freeLeft} 次，解锁后生成完整全文并可下载 Word`
                            : '免费预览次数已用完，解锁后生成完整全文并可下载 Word'}
                    </p>
                    {giftText ? <p className={styles.paywallGift}>{giftText}</p> : null}
                    <div className={styles.paywallPrice} role="status">
                        {renderPaywallPrice()}
                    </div>
                </div>
                <div className={styles.paywallActions}>
                    {canContinue ? (
                        <button
                            type="button"
                            className={styles.ghostBtn}
                            onClick={handleContinue}
                            disabled={!!typingId}
                        >
                            <PlayCircleOutlined />
                            继续预览
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={() => openPay('full')}
                    >
                        <CrownOutlined />
                        {payMode === 'upgrade' ? '补差价解锁全文' : '解锁全篇'}
                    </button>
                </div>
            </div>
        </div>
    );

    const resultNo = activeQuote?.orderNo || uid;

    const renderReadingPane = () => (
        <main className={styles.readingPane}>
            <div className={styles.paperScroll} ref={contentAreaRef}>
                <article className={styles.paper}>
                    <div className={styles.runningHead}>
                        <span>
                            {typeLabel} · <span className={styles.runningHeadAccent}>预览稿</span>
                        </span>
                        {Number.isFinite(words) && words > 0 ? (
                            <span>约 {words.toLocaleString('zh-CN')} 字</span>
                        ) : null}
                    </div>
                    <h1 className={styles.paperTitle}>{docTitle}</h1>

                    {purchaseMode !== 'UNPAID' ? (
                        <div className={styles.paidBox}>
                            <span>
                                <CheckCircleFilled />{' '}
                                {PAID_MODES.includes(purchaseMode)
                                    ? '该订单已付款，全文正在生成'
                                    : '该订单已单章解锁，可补差价解锁全文'}
                            </span>
                            <Button
                                size="small"
                                onClick={() =>
                                    navigate(`/order/result?no=${encodeURIComponent(resultNo)}`)
                                }
                            >
                                查看订单
                            </Button>
                        </div>
                    ) : null}

                    {previewError ? (
                        <div className={styles.errorBox} role="alert">
                            <span>{previewError}</span>
                            <button type="button" className={styles.retryBtn} onClick={handleRetry}>
                                <ReloadOutlined />
                                重试
                            </button>
                        </div>
                    ) : null}

                    {renderBlocks()}

                    {notice ? (
                        <div className={styles.noticeBox} role="status" aria-live="polite">
                            {notice}
                        </div>
                    ) : null}

                    {blocks.length === 0 && !typingId && !previewError && !notice ? (
                        <div className={styles.preparing}>正在读取预览进度…</div>
                    ) : null}

                    {blocks.length > 0 && !typingId ? renderPaywall() : null}

                    <div className={styles.paperFoot}>
                        <InfoCircleOutlined />
                        内容由 AI 生成，仅供参考；预览阶段图表以占位显示，解锁后生成
                    </div>
                </article>
            </div>
        </main>
    );

    return (
        <>
            {createPortal(
                <div className={styles.overlay}>
                    <header className={styles.topbar}>
                        <div className={styles.topbarLeft}>
                            <button
                                type="button"
                                className={styles.backBtn}
                                onClick={handleBack}
                                aria-label="返回修改大纲"
                            >
                                <ArrowLeftOutlined />
                                <span className={styles.backText}>返回修改</span>
                            </button>
                            <span className={styles.divider} />
                            <span className={styles.docTag}>{typeLabel}</span>
                            <h2 className={styles.docTitle} title={docTitle}>
                                {docTitle}
                            </h2>
                        </div>
                        <div className={styles.topbarRight}>
                            <button
                                type="button"
                                className={cx(styles.backBtn, styles.tocToggle)}
                                onClick={() => setTocOpen(true)}
                                aria-label="打开目录"
                            >
                                <ListTree size={15} />
                                <span className={styles.backText}>目录</span>
                            </button>
                            <div className={styles.meter} aria-label="免费预览次数">
                                <span className={styles.meterDots}>
                                    {Array.from({ length: maxPreview }, (_, i) => (
                                        <span
                                            key={i}
                                            className={styles.meterDot}
                                            data-on={i < doneCount ? '' : undefined}
                                            data-live={
                                                (typingId || notice) && i === doneCount
                                                    ? ''
                                                    : undefined
                                            }
                                        />
                                    ))}
                                </span>
                                <span>
                                    {typingId
                                        ? '正在生成预览…'
                                        : notice
                                          ? '正在重新连接…'
                                          : `免费预览 ${doneCount} / ${maxPreview}`}
                                </span>
                            </div>
                            <button
                                type="button"
                                className={styles.unlockBtn}
                                onClick={() => openPay('full')}
                            >
                                <CrownOutlined />
                                {payMode === 'upgrade' ? '补差价' : '解锁全篇'}
                            </button>
                        </div>
                    </header>

                    <div className={styles.workspace}>
                        {renderToc()}
                        {renderReadingPane()}
                    </div>
                </div>,
                portalContainer(),
            )}

            <Drawer
                open={tocOpen}
                onClose={() => setTocOpen(false)}
                placement="left"
                width="min(86vw, 320px)"
                closable={false}
                zIndex={1060}
                rootClassName={styles.drawer}
            >
                {renderToc()}
            </Drawer>

            <PayDialog
                open={pay.open}
                onClose={() => setPay((prev) => ({ ...prev, open: false }))}
                uid={uid}
                quote={activeQuote}
                title={docTitle}
                queryType={effectiveType}
                initialPlan={pay.plan}
                initialChapter={pay.chapter}
                chapterPrices={chapterPrices}
                recreate={payMode === 'unlock' ? recreate : undefined}
                mode={payMode}
                zIndex={1100}
            />
        </>
    );
}

function MissingOrder({ onBack }) {
    return createPortal(
        <div className={styles.overlay}>
            <header className={styles.topbar}>
                <div className={styles.topbarLeft}>
                    <button type="button" className={styles.backBtn} onClick={onBack}>
                        <ArrowLeftOutlined />
                        <span className={styles.backText}>返回修改</span>
                    </button>
                </div>
            </header>
            <div className={styles.workspace}>
                <div className={styles.prepWrap}>
                    <div className={styles.errorBox}>订单信息缺失，请返回上一步重新确认大纲</div>
                </div>
            </div>
        </div>,
        portalContainer(),
    );
}

/**
 * 最后一步：预览全文 + 解锁付款（全屏沉浸式，Portal 到 .app-container）。
 * 进入即开始首节免费预览并流式显示；返回大纲再回来时恢复已预览内容、接上仍在生成的一节，不会多开预览。
 *
 * @param {object} props
 * @param {object} props.quote createOrder 返回的 OrderQuote（uid / orderNo / 价格 / 数量 / addonServices）
 * @param {string} props.title 题目
 * @param {string} props.queryType PAPER / OPENING_REPORT / LITERATURE_REVIEW
 * @param {string} props.model normal / academic
 * @param {Array} props.outlines 提交下单的大纲
 * @param {() => void} props.onBack 返回大纲编辑
 * @param {(quote: object) => void} props.onQuoteChange 付款弹窗改附加服务重新建单后回调新报价
 * @param {object} [props.createPayload] 原始下单请求体（有它时重新建单完全沿用，含参考文献 customRef）
 *
 * 会话按订单 uid 区分：父组件换了新订单会重置预览；本组件自己因附加服务重建的订单不重置。
 */
export default function PreviewStep(props) {
    const ownUids = useRef(new Set());
    const uid = props.quote?.uid || '';
    const [session, setSession] = useState({ key: uid || 'pending', last: uid });
    let sessionKey = session.key;
    if (uid !== session.last) {
        const next = {
            key: uid && !ownUids.current.has(uid) ? uid : session.key,
            last: uid,
        };
        sessionKey = next.key;
        setSession(next);
    }
    if (!uid) return <MissingOrder onBack={props.onBack} />;
    return <PreviewSession key={sessionKey} {...props} ownUids={ownUids} />;
}
